import { RawDraftContentState, convertFromRaw, EditorState, ContentState } from "draft-js";

/**
 * Attempts to parse a string as a RawDraftContentState and convert it to a ContentState.
 *
 * @param content - The string to be parsed as a RawDraftContentState.
 * @returns A boolean indicating whether the parsing and conversion were successful.
 *
 * @remarks
 * This function attempts to parse the input string as JSON and then convert it to a
 * DraftJS ContentState. It returns true if both operations succeed, false otherwise.
 * Any JSON parsing errors or issues with converting to ContentState will result in
 * a return value of false.
 *
 * @example
 * ```typescript
 * const isValid = tryParseDraftJsContent('{"blocks":[{"key":"a","text":"Hello"}]}');
 * console.log(isValid); // true or false
 * ```
 */
export const tryParseDraftJsContent = (content: string): boolean => {
    try {
        const parsedContent = JSON.parse(content) as RawDraftContentState;
        const contentState = convertFromRaw(parsedContent);
        return Boolean(contentState);
    } catch (_) {
        return false;
    }
};

/**
 * Creates an EditorState from a plain text string.
 *
 * @param text - The plain text to convert into an EditorState.
 * @returns An EditorState instance containing the provided text.
 *
 * @remarks
 * This function attempts to create a ContentState from the input text and then
 * wraps it in an EditorState. If any error occurs during this process, it returns
 * an empty EditorState.
 *
 * @example
 * ```typescript
 * const editorState = createEditorStateFromText('Hello, world!');
 * ```
 */
export const createEditorStateFromText = (text: string): EditorState => {
    try {
        const convertedContent = ContentState.createFromText(text);
        return EditorState.createWithContent(convertedContent);
    } catch (_error) {
        return EditorState.createEmpty();
    }
};

/**
 * Creates an EditorState from a string representation of a RawDraftContentState.
 *
 * @param content - A string representation of a RawDraftContentState.
 * @returns An EditorState instance created from the provided content, or an empty EditorState if parsing fails.
 *
 * @remarks
 * This function first attempts to parse the input string as a RawDraftContentState using our
 * `tryParseDraftJsContent` method. If successful, it creates an EditorState from the parsed content.
 * If parsing fails, it returns an empty EditorState.
 *
 * @example
 * ```typescript
 * const rawContent = '{"blocks":[{"key":"a","text":"Hello, DraftJS!"}]}';
 * const editorState = createEditorStateFromRawDraftJs(rawContent);
 * ```
 */
export const createEditorStateFromRawDraftJs = (content: string): EditorState => {
    if (tryParseDraftJsContent(content)) {
        const rawContent = JSON.parse(content) as RawDraftContentState;
        return EditorState.createWithContent(convertFromRaw(rawContent));
    }
    return EditorState.createEmpty();
};
